import React, { useContext, useState } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Typography,
  Button,
} from '@mui/material';
import {
  gray100,
  gray80,
  white,
  secondaryButtonBackgroundColor,
  tertiaryButtonBackgroundColor,
} from '../../../constants/colors';
import { COMPANY_STATUS_SUSPENDED } from '../../../constants/labels';
import { suspendInvitedLabels } from '../../../constants/suspendInvited';
import { deleteInvitedCompany } from '../../../api/firebase/firestore/company/deleteCompany';
import { AuthContext } from '../../../contexts/auth';
import { isOperatorAdmin } from '../../../utils/permission';
import StatusChangeConfirmDialog from '../modal/StatusChangeConfirmDialog';
import * as Sentry from '@sentry/react';
import { CompanyType } from '../../../types/company';
import { companyPlan, companyStatus } from '../../../constants/enum';
import {
  COMPANY_TABLE_HEADER_NAME,
  COMPANY_TABLE_HEADER_PLAN,
  COMPANY_TABLE_HEADER_CREATED_AT,
  COMPANY_TABLE_HEADER_USAGE_COUNT,
  COMPANY_TABLE_HEADER_PATTERN_COUNT,
  COMPANY_TABLE_HEADER_STAFF_COUNT,
  COMPANY_TABLE_HEADER_INVITED_EMAIL,
  COMPANY_PLAN_FREE,
  COMPANY_PLAN_STANDARD,
  COMPANY_PLAN_PRO,
  COMPANY_PLAN_CUSTOM,
  COMPANY_PLAN_UNKNOWN,
  COMPANY_STATUS_INVITED,
  COMPANY_LIST_NO_DATA,
} from '../../../constants/labels';
import ReSendInviteMailModal from '../modal/ReSendInviteMailModal';
import CustomSnackbar from '../form/CustomSnackbar';
import useSnackbar from '../../../hooks/useSnackbar';
import CustomLoading from '../form/CustomLoading';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../api/firebase/config';

type CompanyListCardProps = {
  companyList: CompanyType[];
  onRowClick?: (companyId: string, companyName: string) => void;
  /** 削除操作後に一覧を再取得するためのコールバック (#2802) */
  onRefresh?: () => Promise<void>;
};

const styles = {
  paper: {
    padding: '20px',
    backgroundColor: white,
    border: '1px solid #E0E0E0',
    overflowY: 'auto',
    paddingBottom: '220px',
    '@media (max-width: 600px)': {
      paddingBottom: '0',
    },
  },
  tableContainer: {
    overflowX: 'auto',
    width: '100%',
    WebkitOverflowScrolling: 'touch',
    '& table': {
      minWidth: '1000px',
    },
  },
  tableHeader: {
    padding: '6.5px',
    color: gray80,
    fontSize: '14px',
    fontWeight: 400,
    whiteSpace: 'nowrap',
  },
  tableCell: {
    padding: '12px',
    color: gray100,
    fontSize: '14px',
    fontWeight: 400,
    whiteSpace: 'nowrap',
  },
  tableRow: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#F5F5F5',
    },
  },
  noData: {
    textAlign: 'center',
    padding: '40px',
    color: gray80,
  },
  resendButton: {
    height: '32px',
  },
  deleteButton: {
    height: '32px',
    marginLeft: '12px',
  },
  suspendedActionCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '12px',
  },
  actionCellHeader: {
    padding: '6.5px',
    color: gray80,
    fontSize: '14px',
    fontWeight: 400,
    minWidth: '140px',
    whiteSpace: 'nowrap',
  },
  actionCell: {
    padding: '6.5px',
    color: gray100,
    fontSize: '14px',
    fontWeight: 400,
    minWidth: '140px',
    whiteSpace: 'nowrap',
  },
};

const getPlanName = (plan: number): string => {
  switch (plan) {
    case companyPlan.free:
      return COMPANY_PLAN_FREE;
    case companyPlan.standard:
      return COMPANY_PLAN_STANDARD;
    case companyPlan.pro:
      return COMPANY_PLAN_PRO;
    case companyPlan.custom:
      return COMPANY_PLAN_CUSTOM;
    default:
      return COMPANY_PLAN_UNKNOWN;
  }
};

const formatDate = (date: Date): string => {
  if (!date) return '-';
  const d = date instanceof Date ? date : new Date(date);
  return d.toLocaleDateString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

const CompanyListCard: React.FC<CompanyListCardProps> = ({
  companyList,
  onRowClick,
  onRefresh,
}) => {
  const [resendModalOpen, setResendModalOpen] = useState<boolean>(false);
  const [selectedCompany, setSelectedCompany] = useState<CompanyType | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const { errorText, infoText, snackbarOpen, setErrorText, setInfoText, setSnackbarOpen } =
    useSnackbar();
  // 削除ダイアログ (#2802)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [deleteTarget, setDeleteTarget] = useState<CompanyType | null>(null);
  const [deleteProcessing, setDeleteProcessing] = useState<boolean>(false);
  const { user } = useContext(AuthContext);
  // 削除ボタンは運営管理者のみ表示 (#2802)
  const canDeleteCompany = isOperatorAdmin(user?.role);

  const handleResendClick = (
    e: React.MouseEvent<HTMLButtonElement>,
    company: CompanyType,
  ): void => {
    e.stopPropagation();
    setSelectedCompany(company);
    setResendModalOpen(true);
  };

  const handleResendClose = (): void => {
    setResendModalOpen(false);
    setSelectedCompany(null);
  };

  // 企業削除ハンドラ (#2802): 利用停止中の企業のみ削除可能
  const handleDeleteClick = (
    e: React.MouseEvent<HTMLButtonElement>,
    company: CompanyType,
  ): void => {
    e.stopPropagation();
    setDeleteTarget(company);
    setDeleteDialogOpen(true);
  };

  const handleDeleteDialogClose = (): void => {
    if (deleteProcessing) return;
    setDeleteDialogOpen(false);
  };

  const handleDeleteConfirm = async (): Promise<void> => {
    if (!deleteTarget || !user) return;
    setDeleteProcessing(true);
    try {
      await deleteInvitedCompany(deleteTarget.company_id, deleteTarget.invited_email, user.uid);
      setDeleteDialogOpen(false);
      setInfoText(suspendInvitedLabels.companyDeleteSuccess);
      setSnackbarOpen(true);
      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      Sentry.captureException(error);
      setErrorText(suspendInvitedLabels.companyDeleteError);
      setSnackbarOpen(true);
    } finally {
      setDeleteProcessing(false);
    }
  };

  const handleResendEmail = async (): Promise<void> => {
    if (!selectedCompany || !selectedCompany.invited_email) return;

    setLoading(true);
    try {
      const sendCompanyInvitation = httpsCallable<
        { companyName: string; userName: string; email: string },
        { success: boolean; message: string }
      >(functions, 'sendCompanyInvitation');

      const result = await sendCompanyInvitation({
        companyName: selectedCompany.company_name,
        userName: '',
        email: selectedCompany.invited_email,
      });

      if (result.data.success) {
        handleResendClose();
        setInfoText('招待メールを再送しました。');
        setSnackbarOpen(true);
      } else {
        throw new Error('メール送信に失敗しました。');
      }
    } catch (error) {
      setErrorText('メールの送信に失敗しました。');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper sx={styles.paper} elevation={0}>
      <CustomLoading loading={loading} />
      {companyList.length === 0 ? (
        <Box sx={styles.noData}>
          <Typography>{COMPANY_LIST_NO_DATA}</Typography>
        </Box>
      ) : (
        <TableContainer sx={styles.tableContainer}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={styles.tableHeader}>{COMPANY_TABLE_HEADER_NAME}</TableCell>
                <TableCell sx={styles.tableHeader}>{COMPANY_TABLE_HEADER_PLAN}</TableCell>
                <TableCell sx={styles.tableHeader}>{COMPANY_TABLE_HEADER_CREATED_AT}</TableCell>
                <TableCell sx={styles.tableHeader}>{COMPANY_TABLE_HEADER_USAGE_COUNT}</TableCell>
                <TableCell sx={styles.tableHeader}>{COMPANY_TABLE_HEADER_PATTERN_COUNT}</TableCell>
                <TableCell sx={styles.tableHeader}>{COMPANY_TABLE_HEADER_STAFF_COUNT}</TableCell>
                <TableCell sx={styles.tableHeader}>{COMPANY_TABLE_HEADER_INVITED_EMAIL}</TableCell>
                <TableCell sx={styles.actionCellHeader}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {companyList.map(company => (
                <TableRow
                  key={company.company_id}
                  sx={styles.tableRow}
                  onClick={(): void => onRowClick?.(company.company_id, company.company_name)}
                >
                  <TableCell sx={styles.tableCell}>{company.company_name}</TableCell>
                  <TableCell sx={styles.tableCell}>{getPlanName(company.plan)}</TableCell>
                  <TableCell sx={styles.tableCell}>
                    {company.status === companyStatus.invited
                      ? COMPANY_STATUS_INVITED
                      : formatDate(company.created_at)}
                  </TableCell>
                  <TableCell sx={styles.tableCell}>
                    {(company.total_ocr_count ?? 0).toLocaleString()}
                  </TableCell>
                  <TableCell sx={styles.tableCell}>{company.current_pattern_count ?? 0}</TableCell>
                  <TableCell sx={styles.tableCell}>{company.current_user_count ?? 0}</TableCell>
                  <TableCell sx={styles.tableCell}>{company.invited_email || '-'}</TableCell>
                  <TableCell align="right" sx={styles.actionCell}>
                    {company.status === companyStatus.invited && company.invited_email && (
                      <Button
                        sx={[secondaryButtonBackgroundColor, styles.resendButton]}
                        onClick={(e): void => handleResendClick(e, company)}
                      >
                        招待メールを再送
                      </Button>
                    )}
                    {/* 利用停止中の企業 #2802: 「利用停止中」テキスト + 「削除」ボタン（運営管理者のみ） */}
                    {company.status === companyStatus.suspended && (
                      <Box sx={styles.suspendedActionCell}>
                        <Box component="span" sx={{ color: gray80 }}>
                          {COMPANY_STATUS_SUSPENDED}
                        </Box>
                        {canDeleteCompany && (
                          <Button
                            sx={[tertiaryButtonBackgroundColor, styles.deleteButton]}
                            onClick={(e): void => handleDeleteClick(e, company)}
                          >
                            削除
                          </Button>
                        )}
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ReSendInviteMailModal
        open={resendModalOpen}
        onClose={handleResendClose}
        onSend={handleResendEmail}
        title={selectedCompany?.invited_email || ''}
      />

      {/* 企業削除確認ダイアログ #2802 */}
      <StatusChangeConfirmDialog
        open={deleteDialogOpen}
        title={suspendInvitedLabels.companyDeleteDialogTitle}
        body={suspendInvitedLabels.companyDeleteDialogBody}
        confirmLabel={suspendInvitedLabels.deleteButton}
        processingLabel={suspendInvitedLabels.deleteButtonProcessing}
        processing={deleteProcessing}
        onClose={handleDeleteDialogClose}
        onConfirm={handleDeleteConfirm}
      />

      <CustomSnackbar
        open={snackbarOpen}
        onClose={(): void => setSnackbarOpen(false)}
        text={errorText}
        infoText={infoText}
      />
    </Paper>
  );
};

export default CompanyListCard;
